import React, { useState } from 'react';
import { Alert, Card, Row, Col, Badge, Button, Table } from 'react-bootstrap';

export const ConfigUnpackResults = ({ result }) => {
  if (!result?.data) return null;

  const { data, message, isValid } = result;
  const { validation, dataInfo, extractedVariables } = data;

  // Add safety checks for all properties
  const safeValidation = validation || {};
  const safeDataInfo = dataInfo || {};
  const safeExtractedVariables = extractedVariables || {};

  return (
    <div className="config-unpack-results">
      {/* Status Message */}
      <Alert variant={isValid ? 'success' : 'warning'} className="mb-3">
        <strong>{isValid ? '✅ ' : '⚠️ '}</strong>
        {message}
      </Alert>

      {/* Configuration Summary */}
      <Card className="mb-3">
        <Card.Header><strong className="modal-section-title">📋 Configuration Summary</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Source:</strong> {safeDataInfo.source || 'Unknown'}</p>
              <p><strong>Total Rows:</strong> {safeDataInfo.totalRows || 0}</p>
              <p><strong>Unique Runs:</strong> {safeDataInfo.uniqueRuns || 0}</p>
            </Col>
            <Col md={6}>
              <p><strong>Available Columns:</strong> {safeValidation.availableColumns || 0}</p>
              <p><strong>Run IDs:</strong> {(safeDataInfo.runIds || []).join(', ') || 'None'}</p>
              {(safeDataInfo.missingRefRuns || []).length > 0 && (
                <p><strong>⚠️ Missing Reference Runs:</strong> {safeDataInfo.missingRefRuns.join(', ')}</p>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Validation Results */}
      <Card className="mb-3">
        <Card.Header><strong className="modal-section-title">✓ Column Validation</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <h6>Core Columns {safeValidation.coreColumnsValid ? '✅' : '❌'}</h6>
              {(safeValidation.missingCoreColumns || []).length > 0 ? (
                <div>
                  <small className="text-danger">Missing: {safeValidation.missingCoreColumns.join(', ')}</small>
                </div>
              ) : (
                <small className="text-success">All core columns present</small>
              )}
            </Col>
            <Col md={4}>
              <h6>KPI Columns {safeValidation.kpisValid ? '✅' : '⚠️'}</h6>
              {(safeValidation.missingKPIs || []).length > 0 ? (
                <div>
                  <small className="text-warning">Missing: {(safeValidation.missingKPIs || []).slice(0, 3).join(', ')}
                    {(safeValidation.missingKPIs || []).length > 3 && ` +${safeValidation.missingKPIs.length - 3} more`}
                  </small>
                </div>
              ) : (
                <small className="text-success">All KPI columns present</small>
              )}
            </Col>
            <Col md={4}>
              <h6>Event Variables {safeValidation.eventVarsValid ? '✅' : '⚠️'}</h6>
              {(safeValidation.missingEventVars || []).length > 0 ? (
                <div>
                  <small className="text-warning">Missing: {(safeValidation.missingEventVars || []).slice(0, 3).join(', ')}
                    {(safeValidation.missingEventVars || []).length > 3 && ` +${safeValidation.missingEventVars.length - 3} more`}
                  </small>
                </div>
              ) : (
                <small className="text-success">All event variables present</small>
              )}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Extracted Configuration */}
      <Card>
        <Card.Header><strong className="modal-section-title">⚙️ Extracted Configuration</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Reference Run IDs:</strong> {(safeExtractedVariables.ref_ids || []).join(', ') || 'None'}</p>
              <p><strong>Growth Threshold:</strong> {safeExtractedVariables.gthr || 0}</p>
              <p><strong>Viability Threshold:</strong> {safeExtractedVariables.vthr || 0}</p>
              <p><strong>Max Lag Days:</strong> {safeExtractedVariables.maxlag || 0}</p>
            </Col>
            <Col md={6}>
              <p><strong>KPIs:</strong> {(safeExtractedVariables.kpis || []).length} variables</p>
              <p><strong>Event Variables:</strong> {(safeExtractedVariables.vars_ || []).length} variables</p>
              <p><strong>Scaled Columns:</strong> {(safeExtractedVariables.kcols || []).length} generated</p>
              <p><strong>Instrument Floors:</strong> {Object.keys(safeExtractedVariables.floors || {}).length} specified</p>
            </Col>
          </Row>
          
          {/* Expandable details */}
          <details className="mt-3">
            <summary className="text-primary" style={{cursor: 'pointer'}}>
              <strong>View Detailed Configuration</strong>
            </summary>
            <pre className="small text-muted mt-2" style={{maxHeight: '200px', overflow: 'auto'}}>
              {JSON.stringify(safeExtractedVariables, null, 2)}
            </pre>
          </details>
        </Card.Body>
      </Card>
    </div>
  );
};

export const ScalingReferenceResults = ({ result }) => {
  const [showDetailedStats, setShowDetailedStats] = useState(false);
  const [showReferenceProfile, setShowReferenceProfile] = useState(false);

  if (!result?.data) {
    return (
      <Alert variant="warning">
        <strong>No Scaling & Reference Data</strong>
        <p>ScalingReferenceResults component received no data.</p>
      </Alert>
    );
  }

  const { data, message, isValid } = result;
  
  // Extract data with multiple fallback patterns to handle different API response formats
  const scaledDataInfo = data.scaled_data_info || data.scaledDataInfo || {};
  const scalingStatistics = data.scaling_statistics || data.scalingStatistics || [];
  const referenceProfileInfo = data.reference_profile_info || data.referenceProfileInfo || {};
  const referenceProfileData = data.reference_profile_data || data.referenceProfileData || null;
  const goodRuns = data.good_runs || data.goodRuns || [];
  const referenceRuns = data.reference_runs || data.referenceRuns || 
                       data.ref_ids || referenceProfileInfo.aligned_runs || [];
  const kpiColumns = data.kcols || data.kpi_columns || referenceProfileInfo.kpi_columns || [];

  console.log('ScalingReferenceResults - Enhanced Debug:', {
    scaledDataInfo,
    scalingStatistics,
    referenceProfileInfo,
    referenceProfileData,
    goodRuns,
    referenceRuns,
    kpiColumns,
    fullDataKeys: Object.keys(data),
    message,
    isValid
  });

  return (
    <div className="scaling-reference-results">
      {/* Status Message */}
      <Alert variant={isValid ? 'success' : 'warning'} className="mb-3">
        <strong>{isValid ? '📊 ' : '⚠️ '}</strong>
        {message || 'Scaling & Reference processing completed'}
      </Alert>

      {/* Main Summary Grid */}
      <Row className="mb-3">
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h4 className="text-primary mb-1">{scaledDataInfo.total_rows || 0}</h4>
              <small className="text-muted">Total Data Rows</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h4 className="text-success mb-1">{scaledDataInfo.scaled_columns_added || scalingStatistics.length}</h4>
              <small className="text-muted">KPIs Scaled</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h4 className="text-info mb-1">{goodRuns.length}</h4>
              <small className="text-muted">Good Runs</small>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="text-center h-100">
            <Card.Body>
              <h4 className="text-warning mb-1">{referenceRuns.length}</h4>
              <small className="text-muted">Reference Runs</small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Reference Runs & Profile Information */}
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <strong>🎯 Reference Runs & Profile (ref_ids)</strong>
          <Button 
            variant="outline-primary" 
            size="sm"
            onClick={() => setShowReferenceProfile(!showReferenceProfile)}
          >
            {showReferenceProfile ? 'Hide' : 'Show'} Details
          </Button>
        </Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6 className="text-secondary mb-3">Reference Runs Used</h6>
              {referenceRuns.length > 0 ? (
                <div>
                  {referenceRuns.map((runId, index) => (
                    <Badge key={index} variant="primary" className="mr-1 mb-1">
                      Run {runId}
                    </Badge>
                  ))}
                </div>
              ) : (
                <p className="text-muted">No reference runs specified</p>
              )}
              
              <h6 className="text-secondary mt-3 mb-2">Good Runs Identified</h6>
              {goodRuns.length > 0 ? (
                <div>
                  {goodRuns.slice(0, 10).map((runId, index) => (
                    <Badge key={index} variant="success" className="mr-1 mb-1">
                      Run {runId}
                    </Badge>
                  ))}
                  {goodRuns.length > 10 && (
                    <Badge variant="light" className="mr-1 mb-1">
                      +{goodRuns.length - 10} more
                    </Badge>
                  )}
                </div>
              ) : (
                <p className="text-muted">No good runs identified</p>
              )}
            </Col>
            <Col md={6}>
              <h6 className="text-secondary mb-3">Profile Statistics</h6>
              <p><strong>Profile Length:</strong> {referenceProfileInfo.profile_length || 0} time points</p>
              <p><strong>Aligned Runs:</strong> {(referenceProfileInfo.aligned_runs || []).length}</p>
              <p><strong>Total Ref Runs:</strong> {referenceProfileInfo.total_reference_runs || referenceRuns.length}</p>
              
              {kpiColumns.length > 0 && (
                <div>
                  <h6 className="text-secondary mt-3 mb-2">KPI Columns (kcols)</h6>
                  <div style={{ maxHeight: '100px', overflowY: 'auto' }}>
                    {kpiColumns.map((kpi, index) => (
                      <Badge key={index} variant="info" className="mr-1 mb-1">
                        {kpi}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </Col>
          </Row>

          {showReferenceProfile && (
            <div className="mt-3">
              <h6 className="text-secondary">Reference Profile Details</h6>
              <div className="alert alert-light">
                <Row>
                  <Col md={4}>
                    <strong>Profile Length:</strong> {referenceProfileInfo.profile_length || 'N/A'}
                  </Col>
                  <Col md={4}>
                    <strong>KPI Count:</strong> {kpiColumns.length}
                  </Col>
                  <Col md={4}>
                    <strong>Ref Runs:</strong> {referenceRuns.length}
                  </Col>
                </Row>
                {referenceProfileInfo.aligned_runs && (
                  <div className="mt-2">
                    <strong>Aligned Runs:</strong> {referenceProfileInfo.aligned_runs.join(', ')}
                  </div>
                )}
              </div>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Normalized Data Information (df_norm) */}
      <Card className="mb-3">
        <Card.Header><strong className="modal-section-title">📊 Normalized Data Information (df_norm)</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6 className="text-secondary mb-3">Dataset Dimensions</h6>
              <p><strong>Total Rows:</strong> {scaledDataInfo.total_rows || 0}</p>
              <p><strong>Total Columns:</strong> {scaledDataInfo.total_columns || 0}</p>
              <p><strong>New Scaled Columns:</strong> {scaledDataInfo.scaled_columns_added || scalingStatistics.length}</p>
            </Col>
            <Col md={6}>
              <h6 className="text-secondary mb-3">Processing Summary</h6>
              <p><strong>Good Runs Used:</strong> {goodRuns.length}</p>
              <p><strong>Reference Runs:</strong> {referenceRuns.length}</p>
              <p><strong>KPI Columns:</strong> {kpiColumns.length}</p>
            </Col>
          </Row>
          
          {scaledDataInfo.total_rows > 0 && (
            <Alert variant="info" className="mt-3">
              <strong>📈 Normalized DataFrame Generated</strong>
              <p className="mb-0">
                A normalized DataFrame (df_norm) with {scaledDataInfo.total_rows} rows and {scaledDataInfo.scaled_columns_added || scalingStatistics.length} scaled KPI columns 
                has been generated using {goodRuns.length} good runs for scaling calculations.
              </p>
            </Alert>
          )}
        </Card.Body>
      </Card>

      {/* KPI Scaling Statistics */}
      {scalingStatistics.length > 0 && (
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <strong>📈 KPI Scaling Statistics</strong>
            <Button 
              variant="outline-info" 
              size="sm"
              onClick={() => setShowDetailedStats(!showDetailedStats)}
            >
              {showDetailedStats ? 'Hide' : 'Show'} Detailed Grid
            </Button>
          </Card.Header>
          <Card.Body>
            {!showDetailedStats ? (
              <div>
                <p><strong>Scaled KPIs:</strong> {scalingStatistics.length}</p>
                <div>
                  {scalingStatistics.slice(0, 8).map((stat, index) => (
                    <Badge key={index} variant="secondary" className="mr-1 mb-1">
                      {stat.kpi_name || stat.kpi}
                    </Badge>
                  ))}
                  {scalingStatistics.length > 8 && (
                    <Badge variant="light" className="mr-1 mb-1">
                      +{scalingStatistics.length - 8} more
                    </Badge>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>KPI Name</th>
                      <th>Median</th>
                      <th>MAD</th>
                      <th>Scaled Column</th>
                      <th>Good Runs Used</th>
                    </tr>
                  </thead>
                  <tbody>
                    {scalingStatistics.map((stat, index) => (
                      <tr key={index}>
                        <td><strong>{stat.kpi_name || stat.kpi}</strong></td>
                        <td>{typeof stat.median === 'number' ? stat.median.toFixed(3) : stat.median || 'N/A'}</td>
                        <td>{typeof stat.mad === 'number' ? stat.mad.toFixed(3) : stat.mad || 'N/A'}</td>
                        <td>{stat.scaled_column || stat.scaledColumn || `${stat.kpi_name || stat.kpi}_scaled`}</td>
                        <td>{stat.good_runs_used || stat.goodRunsUsed || goodRuns.length}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Reference Profile Data Debug Section */}
      {data.reference_profile_data && (
        <Card className="mb-3">
          <Card.Header>
            <strong>🔍 Reference Profile Data (Debug)</strong>
          </Card.Header>
          <Card.Body>
            {data.reference_profile_data.error ? (
              <Alert variant="danger">
                <strong>Error:</strong> {data.reference_profile_data.error}
              </Alert>
            ) : (
              <div>
                <Row className="mb-3">
                  <Col md={4}>
                    <h6>Profile Shape</h6>
                    <p>Rows: {data.reference_profile_data.profile_shape?.[0] || 'N/A'}</p>
                    <p>Columns: {data.reference_profile_data.profile_shape?.[1] || 'N/A'}</p>
                  </Col>
                  <Col md={4}>
                    <h6>Index Range</h6>
                    <p>Min: {data.reference_profile_data.profile_index_range?.min?.toFixed(2) || 'N/A'}</p>
                    <p>Max: {data.reference_profile_data.profile_index_range?.max?.toFixed(2) || 'N/A'}</p>
                  </Col>
                  <Col md={4}>
                    <h6>Columns</h6>
                    <div style={{maxHeight: '100px', overflowY: 'auto'}}>
                      {(data.reference_profile_data.profile_columns || []).map((col, index) => (
                        <Badge key={index} variant="info" className="mr-1 mb-1">{col}</Badge>
                      ))}
                    </div>
                  </Col>
                </Row>
                
                {data.reference_profile_data.profile_sample_data && (
                  <div className="mb-3">
                    <h6>Sample Data (First 5 rows)</h6>
                    <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>Days</th>
                            {(data.reference_profile_data.profile_columns || []).map((col, index) => (
                              <th key={index}>{col}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(data.reference_profile_data.profile_sample_data || {}).map(([day, values]) => (
                            <tr key={day}>
                              <td><strong>{day}</strong></td>
                              {(data.reference_profile_data.profile_columns || []).map((col, index) => (
                                <td key={index}>{values[col]?.toFixed(3) || 'N/A'}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
                
                {data.reference_profile_data.profile_statistics && (
                  <div className="mb-3">
                    <h6>KPI Statistics</h6>
                    <div style={{maxHeight: '200px', overflowY: 'auto'}}>
                      <Table striped bordered hover size="sm">
                        <thead>
                          <tr>
                            <th>KPI</th>
                            <th>Mean</th>
                            <th>Std</th>
                            <th>Min</th>
                            <th>Max</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(data.reference_profile_data.profile_statistics || {}).map(([kpi, stats]) => (
                            <tr key={kpi}>
                              <td><strong>{kpi}</strong></td>
                              <td>{stats.mean?.toFixed(3) || 'N/A'}</td>
                              <td>{stats.std?.toFixed(3) || 'N/A'}</td>
                              <td>{stats.min?.toFixed(3) || 'N/A'}</td>
                              <td>{stats.max?.toFixed(3) || 'N/A'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {/* Expandable complete data view */}
      <details className="mt-3">
        <summary className="text-primary" style={{cursor: 'pointer'}}>
          <strong>View Complete Step 2 Data (Debug)</strong>
        </summary>
        <pre className="small text-muted mt-2" style={{maxHeight: '300px', overflow: 'auto'}}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};

export const WeightsMetricResults = ({ result }) => {
  if (!result?.data) return null;

  const { data, message, isValid } = result;
  const { weightsInfo, metricInfo, runSignatures } = data;

  // Add safety checks for all properties
  const safeWeightsInfo = weightsInfo || {};
  const safeMetricInfo = metricInfo || {};
  const safeRunSignatures = runSignatures || {};

  // Add fallback logic for blend_alpha and blend_auroc
  const blendAlpha = safeWeightsInfo.blend_alpha !== undefined && safeWeightsInfo.blend_alpha !== 0 
    ? safeWeightsInfo.blend_alpha 
    : (safeWeightsInfo.alpha || 0.0); // Default to 0.5 if no data
    
  const blendAuroc = safeWeightsInfo.blend_auroc !== undefined && safeWeightsInfo.blend_auroc !== 0 
    ? safeWeightsInfo.blend_auroc 
    : (safeMetricInfo.metric_auroc || 0.0); // Use metric AUROC as fallback

  return (
    <div className="weights-metric-results">
      {/* Status Message */}
      <Alert variant={isValid ? 'success' : 'warning'} className="mb-3">
        <strong>{isValid ? '✅ ' : '⚠️ '}</strong>
        {message}
      </Alert>

      {/* Weight Analysis */}
      <Card className="mb-3">
        <Card.Header><strong className="modal-section-title">⚖️ Weight Blending Analysis</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={4}>
              <p><strong>Blend Alpha (α):</strong> {blendAlpha?.toFixed(3) || 'N/A'}</p>
              <p><strong>Blend AUROC:</strong> {blendAuroc?.toFixed(3) || 'N/A'}</p>
            </Col>
            <Col md={4}>
              <p><strong>Total Weights:</strong> {safeWeightsInfo.total_weights?.toFixed(3) || 'N/A'}</p>
              <p><strong>KPIs Used:</strong> {(safeWeightsInfo.kpi_weights || []).length}</p>
            </Col>
            <Col md={4}>
              <div>
                <p><strong>Weight Method:</strong></p>
                <small className="text-muted">
                  α = {(blendAlpha || 0).toFixed(3)} Linear + {(1 - (blendAlpha || 0)).toFixed(3)} Gradient Boosting
                </small>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Metric Selection */}
      <Card className="mb-3">
        <Card.Header><strong className="modal-section-title">📏 Distance Metric Selection</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <h6>Weight Configuration</h6>
              <p><strong>Total Weights:</strong> {Object.keys(safeWeightsInfo).length}</p>
              <p><strong>Weight Sum:</strong> {
                (() => {
                  const sum = Object.values(safeWeightsInfo).reduce((a, b) => a + (parseFloat(b) || 0), 0);
                  return typeof sum === 'number' ? sum.toFixed(3) : '0.000';
                })()
              }</p>
            </Col>
            <Col md={6}>
              <p><strong>All Metric Performance:</strong></p>
              {Object.entries(safeMetricInfo.tested_metrics || {}).map(([metric, score]) => (
                <div key={metric}>
                  <Badge 
                    variant={metric === safeMetricInfo.chosen_metric ? 'success' : 'secondary'} 
                    className="mr-2 mb-1"
                  >
                    {metric.toUpperCase()}: {score.toFixed(3)}
                    {metric === safeMetricInfo.chosen_metric && ' ⭐'}
                  </Badge>
                </div>
              ))}
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Run Signatures Summary */}
      <Card className="mb-3">
        <Card.Header><strong className="modal-section-title">🔢 Run Signatures Summary</strong></Card.Header>
        <Card.Body>
          <Row>
            <Col md={6}>
              <p><strong>Total Runs:</strong> {safeRunSignatures.total_runs || 0}</p>
              <p><strong>Good Runs:</strong> {safeRunSignatures.good_runs || 0}</p>
              <p><strong>Bad Runs:</strong> {safeRunSignatures.bad_runs || 0}</p>
            </Col>
            <Col md={6}>
              <p><strong>KPI Columns Used:</strong></p>
              <div style={{maxHeight: '100px', overflow: 'auto'}}>
                {(safeRunSignatures.kpi_columns_used || []).map((col, index) => (
                  <Badge key={index} variant="info" className="mr-1 mb-1">{col}</Badge>
                ))}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* KPI Weights Details */}
      {(safeWeightsInfo.kpi_weights || []).length > 0 && (
        <Card className="mb-3">
          <Card.Header><strong className="modal-section-title">📊 Individual KPI Weights</strong></Card.Header>
          <Card.Body>
            <div style={{maxHeight: '300px', overflow: 'auto'}}>
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>KPI</th>
                    <th>Linear Weight</th>
                    <th>GB Weight</th>
                    <th>Final Weight</th>
                    <th>Relative Importance</th>
                  </tr>
                </thead>
                <tbody>
                  {safeWeightsInfo.kpi_weights.map((kpi, index) => {
                    const maxWeight = Math.max(...safeWeightsInfo.kpi_weights.map(k => k.blended_weight));
                    const relativeImportance = (kpi.blended_weight / maxWeight * 100);
                    
                    return (
                      <tr key={index}>
                        <td><small>{kpi.kpi}</small></td>
                        <td><small>{kpi.linear_weight.toFixed(4)}</small></td>
                        <td><small>{kpi.gradient_boosting_weight.toFixed(4)}</small></td>
                        <td><strong><small>{kpi.blended_weight.toFixed(4)}</small></strong></td>
                        <td>
                          <div className="progress" style={{height: '15px'}}>
                            <div 
                              className="progress-bar" 
                              style={{width: `${relativeImportance}%`}}
                              title={`${relativeImportance.toFixed(1)}%`}
                            ></div>
                          </div>
                          <small>{relativeImportance.toFixed(1)}%</small>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Expandable details */}
      <details className="mt-3">
        <summary className="text-primary" style={{cursor: 'pointer'}}>
          <strong>View Complete Weights & Metric Data</strong>
        </summary>
        <pre className="small text-muted mt-2" style={{maxHeight: '300px', overflow: 'auto'}}>
          {JSON.stringify(data, null, 2)}
        </pre>
      </details>
    </div>
  );
};
